<template>
  <aside class="detail-panel" :data-qa="`accessory-detail-${view.ID}`">
    <div ref="scroll" class="detail-panel__scroll">
      <div v-if="showClose || memberOpen" class="detail-panel__head">
        <button
          v-if="memberOpen"
          class="detail-panel__back"
          type="button"
          data-qa="accessory-detail-back"
          @click="closeMember"
        >
          <icon icon="angle-left" aria-hidden="true" />
          <span>{{ i18n.t('/AccessoryCatalog/BackToPackage') }}</span>
        </button>
        <button
          v-if="showClose"
          class="detail-panel__close"
          type="button"
          :title="i18n.t('/AccessoryCatalog/CloseTooltip')"
          :aria-label="i18n.t('/AccessoryCatalog/CloseTooltip')"
          data-qa="accessory-detail-close"
          @click="$emit('close')"
        >
          <icon icon="close" aria-hidden="true" />
        </button>
      </div>

      <div v-if="images.length" class="detail-panel__gallery">
        <button
          type="button"
          class="detail-panel__zoom"
          :title="i18n.t('/AccessoryCatalog/EnlargeTooltip')"
          :aria-label="i18n.t('/AccessoryCatalog/EnlargeTooltip')"
          data-qa="accessory-detail-enlarge"
          @click="lightboxOpen = true"
        >
          <accessory-thumb :src="images[currentIndex]" :alt="title" class="detail-panel__main" />
        </button>
        <div v-if="images.length > 1" class="detail-panel__thumbs">
          <accessory-thumb
            v-for="(thumb, index) in thumbs"
            :key="thumb.url"
            :src="thumb.url"
            :alt="title"
            compact
            interactive
            :aria-pressed="index === currentIndex"
            :aria-label="thumb.label"
            :class="{ on: index === currentIndex }"
            @click="imageIndex = index"
          />
        </div>
      </div>
      <div v-else-if="!enriched" class="detail-panel__unenriched">
        {{ i18n.t('/AccessoryCatalog/NotInCatalog') }}
      </div>

      <div class="detail-panel__body">
        <h3>{{ title }}</h3>
        <div class="detail-panel__path">
          <template v-if="path">{{ path }} · </template
          >{{ i18n.t('/AccessoryCatalog/ArticleNrLong') }}
          <code>{{ view.ID }}</code>
        </div>
        <!-- The stock note is visible text rather than a title: a title on a div
             never reaches a keyboard or a touch screen -->
        <div v-if="stockLabel" class="detail-panel__stock">
          <span :class="['stock', { 'stock--no': !inStock }]">{{ stockLabel }}</span>
          <span class="detail-panel__stock-meta">{{ i18n.t('/AccessoryCatalog/StockMeta') }}</span>
        </div>

        <div v-if="actionActive" class="detail-panel__action">
          <icon icon="info-plain" aria-hidden="true" />
          <span>{{ actionBanner }}</span>
        </div>

        <template v-if="pon && pon.Description">
          <div class="detail-panel__seclbl">{{ i18n.t('/AccessoryCatalog/Description') }}</div>
          <!-- Description comes from the Pon catalog and can contain HTML -->
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="detail-panel__text" v-html="pon.Description" />
        </template>

        <template v-if="pon && pon.RemarksHtml">
          <div class="detail-panel__seclbl">{{ i18n.t('/AccessoryCatalog/Remarks') }}</div>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="detail-panel__remarks" v-html="pon.RemarksHtml" />
        </template>

        <template v-if="documents.length">
          <div class="detail-panel__seclbl">{{ i18n.t('/AccessoryCatalog/Documents') }}</div>
          <ul class="detail-panel__docs">
            <li v-for="(doc, index) in documents" :key="doc.Url">
              <a
                :href="doc.Url"
                target="_blank"
                rel="noopener noreferrer"
                :data-qa="`accessory-detail-document-${view.ID}-${index}`"
              >
                <icon icon="download-plain" aria-hidden="true" />
                <span>{{ doc.Name || i18n.t('/AccessoryCatalog/DocumentFallback') }}</span>
              </a>
            </li>
          </ul>
        </template>

        <template v-if="members.length">
          <div class="detail-panel__seclbl">{{ i18n.t('/AccessoryCatalog/PackageContent') }}</div>
          <package-content-list
            :members="members"
            :vat="vat"
            :action-active="actionActive"
            @open="openMember"
          />
        </template>

        <div class="detail-panel__seclbl">{{ i18n.t('/AccessoryCatalog/PriceBuildUp') }}</div>
        <accessory-price-build-up
          :accessory="view"
          :vat="vat"
          :show-full-build-up="!memberOpen"
          :is-package-member-line="memberOpen"
          :now="clock"
        />
      </div>
    </div>

    <teleport to="body">
      <accessory-image-lightbox
        v-if="lightboxOpen && images.length"
        :images="images"
        :index="currentIndex"
        :alt="title"
        @update:index="imageIndex = $event"
        @close="lightboxOpen = false"
      />
    </teleport>

    <div v-if="isEditable && !memberOpen" class="detail-panel__foot">
      <app-button
        :data-qa="`accessory-detail-toggle-${accessory.ID}`"
        @click="$emit('toggle', accessory)"
      >
        {{
          isSelected
            ? i18n.t('/AccessoryCatalog/RemoveFromQuote')
            : i18n.t('/AccessoryCatalog/AddToQuote')
        }}
      </app-button>
    </div>
  </aside>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import AccessoryThumb from './AccessoryThumb.vue'
import AccessoryImageLightbox from './AccessoryImageLightbox.vue'
import AccessoryPriceBuildUp from './AccessoryPriceBuildUp.vue'
import PackageContentList from './PackageContentList.vue'
import {
  VAT_EXCL,
  VAT_INCL,
  actionPrice,
  categoryPath,
  galleryImages,
  hasActiveAction,
  isEnriched,
  packageContent,
  packageMemberAsAccessory,
  regularPrice,
} from '@/helpers/ponCatalog'
import { displayTitle } from '@/helpers/ponCatalogFilters'
import dateHelper from '@/helpers/dateHelper'

export default {
  components: {
    Icon,
    AppButton,
    AccessoryThumb,
    AccessoryImageLightbox,
    AccessoryPriceBuildUp,
    PackageContentList,
  },
  props: {
    accessory: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    /**
     * Which price the lines that have room for only one show: VAT_INCL or
     * VAT_EXCL. The price build-up table always shows both.
     */
    vat: {
      type: String,
      default: VAT_INCL,
    },
    /** Shared catalog clock; see mixins/catalogClock */
    now: {
      type: Date,
      default: null,
    },
  },
  emits: ['toggle', 'close'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      imageIndex: 0,
      lightboxOpen: false,
      // Package members opened from the Pakketinhoud list; a stack so a member
      // that is itself a package can be drilled into and backed out of
      memberStack: [],
      // Scroll offset of each level we drilled down from, so going back lands
      // on the package line the user clicked instead of at the top
      scrollOffsets: [],
    }
  },
  computed: {
    clock() {
      return this.now || new Date()
    },
    /** The accessory on screen: the opened package member, or the prop itself */
    view() {
      return this.memberStack[this.memberStack.length - 1] || this.accessory
    },
    memberOpen() {
      return this.memberStack.length > 0
    },
    members() {
      return packageContent(this.view)
    },
    pon() {
      return this.view.PonAccessoryCatalog || null
    },
    enriched() {
      return isEnriched(this.view)
    },
    title() {
      return displayTitle(this.view)
    },
    images() {
      return galleryImages(this.view)
    },
    /**
     * The photo on screen. `imageIndex` is clamped here, where it lives, rather
     * than in the lightbox: a lightbox that quietly clamped its own copy left
     * the panel pointing past the end of a gallery it had since replaced, and
     * the panel then drew the "geen foto" placeholder for an accessory that has
     * photos.
     */
    currentIndex() {
      if (!this.images.length) return 0
      return Math.min(Math.max(this.imageIndex, 0), this.images.length - 1)
    },
    /**
     * The strip switches the hero photo, so each thumb is a control and needs a
     * name saying which photo it picks — the accessory plus its position, since
     * the photos themselves have no titles.
     */
    thumbs() {
      const count = this.images.length
      return this.images.map((url, index) => ({
        url,
        label: `${this.title} ${this.i18n.t('/AccessoryCatalog/ImageCounter', { index: index + 1, count })}`,
      }))
    },
    documents() {
      return this.pon?.Documents || []
    },
    path() {
      return categoryPath(this.view).join(' › ')
    },
    inStock() {
      return (this.pon?.Stock ?? 0) > 0
    },
    stockLabel() {
      if (!this.enriched || this.pon.Stock === null) return null
      return this.inStock
        ? this.i18n.t('/AccessoryCatalog/StockAvailable', { count: this.pon.Stock })
        : this.i18n.t('/AccessoryCatalog/StockUnavailable')
    },
    actionActive() {
      return hasActiveAction(this.view, this.clock)
    },
    action() {
      return actionPrice(this.view, this.clock)
    },
    /** The action price on the side of the VAT line the quote asks for */
    actionAtVat() {
      if (!this.action) return null
      return this.vat === VAT_EXCL ? this.action.excl : this.action.incl
    },
    actionBanner() {
      // Regular catalog price, shown as "in plaats van" when above the action price
      const regular = regularPrice(this.view, this.vat)
      const struck = regular !== null && regular > this.actionAtVat ? regular : null
      return this.i18n.t('/AccessoryCatalog/ActionBanner', {
        price: `${this.i18n.n(this.actionAtVat, 'currency')} ${this.vatLabel}`,
        was: struck
          ? this.i18n.t('/AccessoryCatalog/ActionBannerWas', {
              price: this.i18n.n(struck, 'currency'),
            })
          : '',
        date: dateHelper.formatDate(this.pon.ActionValidTo),
      })
    },
    vatLabel() {
      return this.vat === VAT_EXCL
        ? this.i18n.t('/AccessoryCatalog/ExclVat')
        : this.i18n.t('/AccessoryCatalog/InclVat')
    },
  },
  watch: {
    // Each member has its own gallery
    memberStack() {
      this.imageIndex = 0
      this.lightboxOpen = false
    },
  },
  methods: {
    openMember(index) {
      this.scrollOffsets.push(this.$refs.scroll ? this.$refs.scroll.scrollTop : 0)
      this.memberStack = [
        ...this.memberStack,
        packageMemberAsAccessory(this.members[index], this.actionActive),
      ]
      // A member opens at its own top, not at the offset of the package behind it
      this.restoreScroll(0)
    },
    closeMember() {
      const offset = this.scrollOffsets.pop() || 0
      this.memberStack = this.memberStack.slice(0, -1)
      this.restoreScroll(offset)
    },
    restoreScroll(top) {
      this.$nextTick(() => {
        if (this.$refs.scroll) this.$refs.scroll.scrollTop = top
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// There is no global font-size scale in styles/variables, so the panel names
// the steps it uses here instead of scattering fractional-pixel literals.
$eyebrow-size: 11px;
$meta-size: 12px;
$body-size: 13px;
$text-size: 14px;

.detail-panel {
  background: $white;
  display: grid;
  grid-template-rows: 1fr auto;
  min-height: 0;
  animation: detail-slide 0.18s ease;

  &__scroll {
    overflow-y: auto;
    padding: 0 0 8px;
    min-height: 0;
  }

  &__head {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px 0;
    background: $white;
  }

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px 6px 6px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--color-primary);
    font-size: $body-size;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.14s ease;

    &:hover {
      background: $gray-lightest;
    }

    svg {
      @include icon;
      width: 14px;
      height: 14px;
    }
  }

  &__close {
    // Keeps the close on the right whether or not the back button is there
    margin-left: auto;
    width: 38px;
    height: 38px;
    border: 0;
    border-radius: 50%;
    background: $white;
    color: $gray-darker;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background 0.14s ease;

    &:hover {
      background: $gray-light;
      color: $text-color;
    }

    svg {
      @include icon;
      width: 15px;
      height: 15px;
    }
  }

  &__gallery {
    padding: 0 22px;
  }

  &__zoom {
    display: block;
    width: 100%;
    padding: 0;
    border: 0;
    background: none;
    cursor: zoom-in;
  }

  &__main {
    aspect-ratio: 3 / 2;
    max-height: 300px;
    width: 100%;
  }

  &__thumbs {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    margin-top: 8px;

    .accessory-thumb {
      aspect-ratio: 1;
      cursor: pointer;

      &.on {
        border-color: var(--color-primary);
        box-shadow: 0 0 0 2px var(--color-primary-light);
      }
    }
  }

  &__unenriched {
    margin: 4px 22px 0;
    padding: 12px 14px;
    border: 1px dashed $gray-light;
    border-radius: 10px;
    font-size: $body-size;
    color: $gray-darker;
    line-height: 1.5;
  }

  &__body {
    padding: 18px 22px 0;

    h3 {
      margin: 0 0 6px;
      font-size: 1.2em;
      font-weight: 700;
      color: var(--color-primary);
      line-height: 1.25;
    }
  }

  &__path {
    font-size: $meta-size;
    color: $gray-dark;
    margin-bottom: 6px;

    code {
      font-family: $font-family-mono;
    }
  }

  &__stock {
    font-size: $meta-size;
    margin: 0 0 14px;

    .stock {
      color: $color-success-dark;
      font-weight: 600;

      &--no {
        color: $gray-dark;
        font-weight: 400;
      }
    }

    &-meta {
      // Template whitespace between the two spans is stripped at compile time
      margin-left: 4px;
      color: $gray-dark;
    }

    &-note {
      display: block;
      margin-top: 2px;
      color: $gray-dark;
    }
  }

  &__action {
    display: flex;
    align-items: center;
    gap: 10px;
    background: $color-success-light;
    border: 1px solid rgba($color-success-dark, 0.35);
    border-radius: 10px;
    padding: 11px 14px;
    font-size: $body-size;
    color: $color-success-dark;
    line-height: 1.4;

    svg {
      @include icon;
      width: 15px;
      height: 15px;
      flex: 0 0 auto;
    }
  }

  &__seclbl {
    font-size: $eyebrow-size;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: $gray-dark;
    margin: 20px 0 8px;
  }

  &__text {
    font-size: $text-size;
    color: $text-color;
    line-height: 1.6;

    :deep(p) {
      margin-bottom: 8px;
    }

    :deep(ul) {
      padding-left: 1.2em;
      list-style: disc;
      margin-bottom: 8px;
    }
  }

  &__remarks {
    background: $color-warning-light;
    border: 1px solid $color-warning-border;
    border-radius: 10px;
    padding: 12px 14px;
    font-size: $body-size;
    color: $color-warning-dark;
    line-height: 1.55;

    :deep(p) {
      margin: 0 0 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    :deep(ul) {
      padding-left: 1.2em;
      list-style: disc;
    }

    :deep(strong) {
      font-weight: 700;
    }
  }

  &__docs {
    list-style: none;
    margin: 0;
    padding: 0;

    li + li {
      margin-top: 6px;
    }

    a {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: $text-size;
      line-height: 1.4;
      color: var(--color-primary);
    }

    svg {
      @include icon;
      width: 14px;
      height: 14px;
      flex: 0 0 auto;
    }
  }

  &__foot {
    padding: 14px 22px;
    border-top: 1px solid $gray-light;
    display: flex;
    gap: 10px;
    align-items: center;
    background: $white;

    button {
      flex: 1;
      justify-content: center;
    }
  }
}

@keyframes detail-slide {
  from {
    opacity: 0;
    transform: translateX(16px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}
</style>
